const Controller = require("../common/Controller");
const ConnectionView = require("connect/ConnectionView");
const ServerConfiguration = require("../common/ServerConfiguration");
const Utils = require("../common/Utils");

function AbstractConnectionController() {
    Controller.call(this);

    const self = this;
    const connectionView = new ConnectionView();
    let connectionRequest;
    let totpConnectionRequest;

    /**
     * @override
     * @inheritsDoc
     */
    this.getId = function() {
        return "ConnectionController";
    };

    this.getViews = function() {
        return [connectionView];
    };

    self.subscribeToChannel("Electron", "cancel-connection", function() {
        self.cancelConnection();
    });

    self.subscribeTo("cancel", function() {
        self.cancelConnection();
    });

    self.subscribeTo("connect", function(data) {
        if (!connectionRequest) {
            self.connect(data.hostname);
        }
    });

    self.subscribeTo("check-in-progress", function() {
        if (connectionRequest) {
            connectionView.showConnecting();
            Utils.showLoading(true);
        }
    });

    this.cancelConnection = function() {
        try {
            connectionRequest.abort();
            totpConnectionRequest.abort();
        } catch (e) {
            console.debug("Attempted to abort ajax request, but it had already finished");
        }

        console.debug("Marking ajax request for auth settings as cancelled");
        connectionView.reset();
    };

    this.connect = function(hostname) {
        if (this.validateHostname && !this.validateHostname(hostname)) {
            console.warn("hostname validation failed for " + hostname);
            connectionView.showConnectionFailure();
        } else {
            // Abort any existing connection request (there shouldn't be one, but just to be sure...)
            if (connectionRequest) {
                connectionRequest.abort();
            }

            connectionView.showConnecting();

            console.debug("Performing ajax request for auth settings");

            connectionRequest = $.ajax({
                url : App.getApiRoot() + "authentication/setting",
                cache : false
            }).always(function() {
                // Remove the reference to the promise once we are done
                connectionRequest = undefined;
            });

            $.when(connectionRequest).done(function(response) {
                let version = response.version;

                // BFW firmware versions don't match up with NGF versions, so use the API version if it is available
                if (response.apiVersion) {
                    version = response.apiVersion;
                }

                if (version === "") {
                    version = "6.1.1"; // 6.1.1 servers send back the empty string
                }

                if (version === undefined || !App.getFeatureTable().isServerVersionSupported(version)) {
                    let reason = (version) ? " server version not supported by this client" : " no version returned from server";
                    console.error("Unable to connect to '" + hostname + "' - " + reason);
                    connectionView.reset();
                    navigator.notification.alert($.t("common::connect.notsupported", {serverProductName : Utils.getServerProductName(version)}));
                } else {
                    console.info("Connected to '" + hostname + "'");
                    console.debug("Server version: " + version);

                    if (Utils.isGreaterThanOrEqualToServerVersion(version, "7.1.0")) {
                        createConfiguration(response, hostname, version);

                        if (Utils.isGreaterThanOrEqualToServerVersion(version, "8.0.1")) {
                            if (App.isSSLVPNApp()) {
                                connectToTOTPService();
                            } else {
                                updateSessionWithTOTPSettings(response);
                            }
                        }
                    } else {
                        createLegacyConfiguration(response, hostname, version);
                    }

                    // Preload the image before we change page
                    let preloadDeferred = $.Deferred();

                    if (CudaLaunch.serverConfiguration.customLogo) {
                        let logoSrc = App.getRemoteUrlPrefix() + CudaLaunch.serverConfiguration.customLogo;
                        let tmpImg = new Image();
                        tmpImg.onload = function() {
                            preloadDeferred.resolve();
                        };
                        tmpImg.onerror = function() {
                            preloadDeferred.resolve();
                        };
                        tmpImg.src = logoSrc;
                    } else {
                        preloadDeferred.resolve();
                    }

                    $.when(preloadDeferred).then(function() {
                        $.event.trigger("connectionsuccessful", [CudaLaunch.serverConfiguration]);
                    });
                }
            }).fail(function() {
                console.error("failed to connect to " + hostname);
                connectionView.showConnectionFailure();
            });
        }
    };

    function createConfiguration(ajaxResponse, hostname, version) {
        let settings = {
            hostname : hostname,
            customLogo : ajaxResponse.logoPath,
            customTheme : ajaxResponse.themePath,
            version : version,
            allowRememberMe : ajaxResponse.rememberMeAllowed,
            infoFileName : ajaxResponse.infoFileName,
            welcomeText : ajaxResponse.welcomeText,
            identity : ajaxResponse.identity
        };

        CudaLaunch.serverConfiguration = new ServerConfiguration(settings);
        let session = CudaLaunch.serverConfiguration.createSession(ajaxResponse.sessionKey, ajaxResponse.sessionToken);
        session.isCertOnly = ajaxResponse.certOnly;
    }

    function createLegacyConfiguration(ajaxResponse, hostname, version) {
        // Set up the server config
        let authType = "password";

        if (ajaxResponse.authTypes) {
            authType = ajaxResponse.authTypes[0];
        } else if (ajaxResponse.certOnly === true) {
            authType = "certOnly";
        }

        let settings = {
            hostname : hostname,
            authType : authType,
            customLogo : ajaxResponse.logoPath,
            customTheme : ajaxResponse.themePath,
            version : version,
            allowRememberMe : ajaxResponse.rememberMeAllowed,
            passwdLabel : ajaxResponse.passwdLabel,

            // Pre-7.0 servers don't always send back an info path, so we need to use a default
            infoFileName : (ajaxResponse.infoFileName !== undefined) ? ajaxResponse.infoFileName : "/_intmenu/helpmobile.html",
            welcomeText : ajaxResponse.welcomeText,
            performNAC : ajaxResponse.performNAC || false
        };

        console.debug("Requires NAC check: " + (settings.performNAC ? "yes" : "no"));

        CudaLaunch.serverConfiguration = new ServerConfiguration(settings);
    }

    function updateSessionWithTOTPSettings(response) {
        let session = CudaLaunch.serverConfiguration.getSession();

        if (response.sessionKey && response.sessionToken) {
            session.totpEnabled = true;
            session.totpKey = response.sessionKey;
            session.totpToken = response.sessionToken;
        } else {
            session.totpEnabled = false;
        }
    }

    function connectToTOTPService() {
        if (totpConnectionRequest) {
            totpConnectionRequest.abort();
        }
        totpConnectionRequest = $.ajax({
            url : "/totp_api/authentication/setting",
            cache : false
        }).done(function(response) {
            updateSessionWithTOTPSettings(response);
        }).fail(function() {
            let session = CudaLaunch.serverConfiguration.getSession();
            session.totpEnabled = false;
        }).always(function() {
            // Remove the reference to the promise once we are done
            totpConnectionRequest = undefined;
        });
    }

    $(document).on("appmodulesloaded", function() {
        self.setInitialView(connectionView);
    });
}

Utils.inherits(AbstractConnectionController, Controller);

module.exports = AbstractConnectionController;


//////////////////
// WEBPACK FOOTER
// ./connect/AbstractConnectionController.js
// module id = 31
// module chunks = 0 3